// AŞAMA 2 — SIRALI DÜZENLEME KUYRUĞU. Seçili görevler, listede göründükleri
// sırayla tek tek mevcut EditTaskModal'da açılır. Yeni düzenleme mantığı
// yazılmaz; sadece hangi görevin sırada olduğu tutulur.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { confirmDialog } from "./confirm";

export function useEditQueue({ tasks = [], onFinish, onReload } = {}) {
  const [kuyruk, setKuyruk] = useState(null);
  const gezinme = useRef(false); // GERİ çift tıklama kilidi

  const basla = useCallback((ids, gorunenSira) => {
    const secili = new Set(ids || []);
    if (!secili.size) return;
    const kaynak = (gorunenSira && gorunenSira.length ? gorunenSira : tasks) || [];
    const sirali = kaynak.filter((t) => secili.has(t.id)).map((t) => t.id);
    const eksikKalan = [...secili].filter((id) => !sirali.includes(id));
    // kaydedilen/eksik id LİSTESİ tutulur → ileri-geri gezinmede çift sayım yok.
    setKuyruk({ ids: [...sirali, ...eksikKalan], idx: 0, kaydedilen: [], eksik: [] });
  }, [tasks]);

  const bitir = useCallback((k) => {
    setKuyruk(null);
    // Kural: en az bir kez kaydedilen görev "güncellendi"; hiç kaydedilmeyen
    // (atlanan veya geri dönülüp yine kaydedilmeyen) görev "atlandı".
    const guncellendi = k.kaydedilen.length;
    const eksik = k.eksik.filter((id) => !k.kaydedilen.includes(id)).length;
    const atlandi = Math.max(0, k.ids.length - guncellendi - eksik);
    const p = [];
    if (guncellendi) p.push(`${guncellendi} görev güncellendi`);
    if (atlandi) p.push(`${atlandi} atlandı`);
    if (eksik) p.push(`${eksik} görev artık mevcut değil`);
    toast.success(p.length ? p.join(" · ") : "Düzenleme tamamlandı");
    onFinish?.();
  }, [onFinish]);

  // tip: "kaydedildi" | "atlandi" | "eksik"
  const ilerle = useCallback((tip) => {
    setKuyruk((p) => {
      if (!p) return p;
      const id = p.ids[p.idx];
      const n = {
        ...p,
        idx: p.idx + 1,
        kaydedilen: tip === "kaydedildi" && !p.kaydedilen.includes(id)
          ? [...p.kaydedilen, id] : p.kaydedilen,
        eksik: tip === "eksik" && !p.eksik.includes(id) ? [...p.eksik, id] : p.eksik,
      };
      if (n.idx >= n.ids.length) {
        bitir(n);
        return null;
      }
      return n;
    });
  }, [bitir]);

  // GERİ — bir önceki öğeye dön. Sayaçlara dokunmaz (id listeleri korunur).
  const geri = useCallback(() => {
    if (gezinme.current) return; // hızlı iki tık → tek adım
    gezinme.current = true;
    setTimeout(() => { gezinme.current = false; }, 400);
    setKuyruk((p) => (p && p.idx > 0 ? { ...p, idx: p.idx - 1 } : p));
    // Geri dönülen görev SUNUCUDAKİ son kaydedilmiş hâliyle açılır.
    onReload?.();
  }, [onReload]);

  const iptal = useCallback(async () => {
    if (!kuyruk) return true;
    const kalan = kuyruk.ids.length - kuyruk.idx;
    if (kalan > 0) {
      const ok = await confirmDialog({
        title: "SIRALI DÜZENLEME",
        message: `${kalan} görev düzenlenmedi, çıkılsın mı?`,
        confirmText: "ÇIK",
        cancelText: "VAZGEÇ",
        danger: true,
      });
      if (!ok) return false; // kuyruk kaldığı yerden devam eder
    }
    setKuyruk(null);
    onFinish?.();
    return true;
  }, [kuyruk, onFinish]);

  // Sıradaki görev; silinmişse effect'e gerek kalmadan atlanır.
  const aktifGorev = useMemo(() => {
    if (!kuyruk) return null;
    return tasks.find((t) => t.id === kuyruk.ids[kuyruk.idx]) || null;
  }, [kuyruk, tasks]);

  const queueProps = kuyruk
    ? {
        index: kuyruk.idx + 1,
        total: kuyruk.ids.length,
        onNext: () => ilerle("kaydedildi"),
        onSkip: () => ilerle("atlandi"),
        onBack: kuyruk.idx > 0 ? geri : null,
        onCancelQueue: iptal,
      }
    : null;

  // Kuyrukta olup silinmiş görev → atla (render sırasında değil, çağrı ile).
  const eksikAtla = useCallback(() => ilerle("eksik"), [ilerle]);

  // Sıradaki görev bu arada silinmişse kuyruk takılmasın: otomatik atla.
  useEffect(() => {
    if (kuyruk && !aktifGorev) eksikAtla();
  }, [kuyruk, aktifGorev, eksikAtla]);

  return { kuyruk, basla, aktifGorev, queueProps, eksikAtla, aktif: !!kuyruk };
}
